import type { Baby } from '@/types';

// ─────────────────────────────────────────────────────────────────────
// BabyAvatar — displays the baby's photo if set, falls back to a
// circular initial. Sized via the `size` prop (px). Use anywhere a
// baby's identity is being shown: chips, headers, profile, etc.
// ─────────────────────────────────────────────────────────────────────

interface BabyAvatarProps {
  baby: Baby;
  size?: number; // px, default 32
  className?: string;
}

export function BabyAvatar({ baby, size = 32, className = '' }: BabyAvatarProps) {
  const initial = baby.name.charAt(0).toUpperCase();
  const hasPhoto = !!baby.avatar_url;

  // Tailwind doesn't generate arbitrary sizes from props, so use inline
  // width/height. Border + bg only matter for the fallback state.
  const dim = { width: size, height: size };

  if (hasPhoto) {
    return (
      <img
        src={baby.avatar_url!}
        alt={baby.name}
        style={dim}
        className={
          'rounded-full object-cover border border-primary-100 bg-primary-50 shrink-0 ' +
          className
        }
        // If the image 404s (file deleted, URL stale), fall back to initial.
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.display = 'none';
        }}
      />
    );
  }

  return (
    <div
      style={dim}
      className={
        'rounded-full bg-primary text-white font-display font-semibold flex items-center justify-center shrink-0 ' +
        className
      }
    >
      <span style={{ fontSize: Math.max(11, size * 0.42) }}>{initial}</span>
    </div>
  );
}
